/**
 * The car radio: the real stations of wherever the car is (see
 * `core/audio/radio.ts`), played from an `<audio>` element while the player
 * is at the wheel. R tunes to the next station, and after the last turns it
 * off. Getting out stops it; getting back in, it comes on again where it
 * was, as the player left it — and kept per browser, across visits.
 *
 * Streams are not the sound system's own sounds: they come from other
 * servers, which mostly do not allow a page to process them, so they play
 * straight to the speakers, at the game's master volume and the radio's own
 * (`SoundSettings.radio`, a quarter by default), tapered as the mixer's
 * buses are, and muted with the rest.
 */

import type { Station } from '../../core/audio/radio.js';
import { nextStation } from '../../core/audio/radio.js';
import { countryAt } from '../../core/geo/country.js';
import { stationsNear } from '../../loaders/radioService.js';

const KEY = 'wsg.radio';
/** How far the car goes before the stations are looked up again, kilometres. */
const RETUNE_KM = 60;

export class CarRadio {
  private readonly audio = new Audio();
  private stations: Station[] = [];
  private index = -1;
  /** Where the stations were looked up. */
  private listAt: { lat: number; lon: number } | undefined;
  private loading: Promise<void> | undefined;
  private inCar = false;
  /** The station last listened to, '' off: what getting in tunes to. */
  private wanted: string;
  /** Stations that failed to play in a row, so a dead dial stops searching. */
  private failed = 0;

  /** `say` puts a line on the screen: the station tuned to, the radio off. */
  constructor(private readonly say: (text: string) => void) {
    this.audio.preload = 'none';
    this.audio.addEventListener('error', () => this.onError());
    let wanted = '';
    try { wanted = localStorage.getItem(KEY) ?? ''; } catch { /* nothing kept */ }
    this.wanted = wanted;
  }

  /** The station on, while at the wheel. */
  get playing(): Station | undefined {
    return this.inCar && this.index >= 0 ? this.stations[this.index] : undefined;
  }

  /**
   * Every frame: whether the player is at the wheel, where, and the volume
   * (0..1, 0 muted): the master's and the radio's own setting together,
   * squared here as the mixer squares each of its sliders.
   */
  update(inCar: boolean, lat: number, lon: number, volume: number): void {
    this.audio.volume = Math.max(0, Math.min(1, volume * volume));
    if (inCar === this.inCar) return;
    this.inCar = inCar;
    if (!inCar) {
      this.stop();
      return;
    }
    if (this.wanted === '') return;
    void this.ready(lat, lon).then(() => {
      if (!this.inCar) return;
      const i = this.stations.findIndex(s => s.name === this.wanted);
      this.tune(i >= 0 ? i : this.stations.length > 0 ? 0 : -1, false);
    });
  }

  /** R: the next station, or off after the last. */
  next(lat: number, lon: number): void {
    if (!this.inCar) return;
    if (this.loading === undefined && this.stale(lat, lon)) this.say('📻 Finding stations…');
    void this.ready(lat, lon).then(() => {
      if (!this.inCar) return;
      if (this.stations.length === 0) {
        this.say('📻 No stations found here');
        return;
      }
      this.failed = 0;
      this.tune(nextStation(this.index, this.stations.length), true);
    });
  }

  private stale(lat: number, lon: number): boolean {
    if (this.listAt === undefined) return true;
    const km = Math.hypot(lat - this.listAt.lat, (lon - this.listAt.lon) * Math.cos((lat * Math.PI) / 180)) * 111;
    return km > RETUNE_KM;
  }

  /** The stations for (lat, lon), looked up if they are not yet. */
  private ready(lat: number, lon: number): Promise<void> {
    if (this.loading !== undefined) return this.loading;
    if (!this.stale(lat, lon) && this.stations.length > 0) return Promise.resolve();
    this.loading = stationsNear(lat, lon, countryAt(lat, lon)).then((stations) => {
      // Somewhere new: what was on is not on this dial.
      const was = this.index >= 0 ? this.stations[this.index]?.name : undefined;
      this.stations = stations;
      this.index = was === undefined ? -1 : stations.findIndex(s => s.name === was);
      if (stations.length > 0) this.listAt = { lat, lon };
    }).finally(() => { this.loading = undefined; });
    return this.loading;
  }

  private tune(index: number, announce: boolean): void {
    this.index = index;
    const station = this.stations[index];
    this.wanted = station?.name ?? '';
    try { localStorage.setItem(KEY, this.wanted); } catch { /* not kept */ }
    if (station === undefined) {
      this.stop();
      if (announce) this.say('📻 Radio off');
      return;
    }
    this.audio.src = station.url;
    void this.audio.play().catch((e: unknown) => {
      // Not allowed yet, on a page come back to in a car: on the first touch.
      if (e instanceof DOMException && e.name === 'NotAllowedError') this.onGesture();
    });
    if (announce) this.say(`📻 ${station.name}${station.local ? ' · local' : ''}  (${index + 1}/${this.stations.length})`);
  }

  private stop(): void {
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.audio.load();
  }

  /** A stream that will not play: on to the next, unless none will. */
  private onError(): void {
    if (!this.inCar || this.index < 0 || !this.audio.hasAttribute('src')) return;
    this.failed++;
    if (this.failed >= this.stations.length) {
      this.say('📻 No station will play');
      this.index = -1;
      this.stop();
      return;
    }
    // The last one failing goes round to the first, not off.
    this.tune((this.index + 1) % this.stations.length, true);
  }

  private onGesture(): void {
    const retry = () => {
      window.removeEventListener('pointerdown', retry, true);
      window.removeEventListener('keydown', retry, true);
      if (this.inCar && this.index >= 0) void this.audio.play().catch(() => undefined);
    };
    window.addEventListener('pointerdown', retry, true);
    window.addEventListener('keydown', retry, true);
  }
}
